/*
  Global input guards to sanitize user input across the app.
  Rules implemented:
  - Block emojis in all inputs and textareas.
  - Numeric-only enforcement for typical numeric fields (type=number/tel, inputmode=numeric/decimal or pattern of digits).
  - Letters-only enforcement for person name fields (heuristic: name/id/aria-label contains 'nome' or explicit data-only="letters"), except organization names (grupo, empresa, razão social, etc.) which may include digits.
  - Block special characters for non-email, non-password inputs (keeps letters, digits and spaces). Accented letters are allowed.
  - Developers can opt-in explicitly using data-only="numeric" | "letters", opt-out with data-skip-guards="true", allow digits in name fields with data-allow-numeric="true", or allow specials with data-allow-special="true".

  This module attaches capturing listeners for 'beforeinput', 'paste' and 'input'.
*/

// --- Emoji detection ---
// Matches a wide range of emoji, symbols used as emoji, variation selectors, and ZWJ
const EMOJI_REGEX = /[\p{Extended_Pictographic}\u200D\uFE0E\uFE0F]|[\u{1F1E6}-\u{1F1FF}]|[\u{1F300}-\u{1F5FF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu;

const removeEmojis = (str) => str.replace(EMOJI_REGEX, "");

// Character sets (with Unicode flag) - allow accented letters via \p{L}
const ONLY_DIGITS = /[^0-9]/g;
const ONLY_LETTERS_AND_SPACE = /[^\p{L}\s]/gu; // letters + spaces
// Date-like fields should accept separators while typing
const ONLY_DATE_CHARS = /[^0-9/-]/g;

function isTextInput(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "TEXTAREA") return true;
  if (tag !== "INPUT") return false;
  const type = (el.getAttribute("type") || "text").toLowerCase();
  // Exclude buttons, checkboxes, etc.
  return ["text", "search", "email", "number", "tel", "password", "url"].includes(type);
}

function isNumericField(el) {
  if (!el) return false;
  const dataset = el.dataset || {};
  if ((dataset.only || "").toLowerCase() === "numeric") return true;
  const type = (el.getAttribute("type") || "").toLowerCase();
  const inputMode = (el.getAttribute("inputmode") || "").toLowerCase();
  const pattern = el.getAttribute("pattern") || "";
  const name = (el.getAttribute("name") || el.id || "").toLowerCase();
  if (type === "number" || type === "tel") return true;
  if (["numeric", "decimal", "tel"].includes(inputMode)) return true;
  // Consider pattern numeric only when pattern is non-empty and explicitly references digits
  if (pattern && (pattern.includes("\\d") || /\[?0-9\]?/.test(pattern))) {
    return true;
  }
  // Common BR field names that should be numeric
  if (/\b(cpf|cnpj|cep|telefone|celular|fone|ddd|numero|número)\b/.test(name)) return true;
  return false;
}

function fieldLabelText(el) {
  if (!el) return "";
  const parts = [];

  const name = el.getAttribute("name");
  const id = el.id;
  const ariaLabel = el.getAttribute("aria-label");

  if (name) parts.push(name);
  if (id) parts.push(id);
  if (ariaLabel) parts.push(ariaLabel);

  if (typeof document !== "undefined") {
    const labelledBy = el.getAttribute("aria-labelledby");
    if (labelledBy) {
      labelledBy.split(/\s+/).forEach((labelId) => {
        const node = document.getElementById(labelId);
        if (node?.textContent) parts.push(node.textContent);
      });
    }

    if (id) {
      try {
        const labelEl = document.querySelector(`label[for="${CSS.escape(id)}"]`);
        if (labelEl?.textContent) parts.push(labelEl.textContent);
      } catch (_) {
        const labelEl = document.querySelector(`label[for="${id}"]`);
        if (labelEl?.textContent) parts.push(labelEl.textContent);
      }
    }
  }

  return parts.join(" ").toLowerCase();
}

/** Nomes de pessoa vs. razão social / grupo — estes podem conter números (ex.: "1001 SERVICOS LTDA"). */
function isOrganizationNameField(el) {
  const label = fieldLabelText(el);
  return /\b(grupo|empresa|raz[aã]o|social|filial|fantasia|cnpj)\b/.test(label);
}

function allowsNumericInNameField(el) {
  const dataset = el?.dataset || {};
  if ((dataset.allowNumeric || "").toLowerCase() === "true") return true;
  return isOrganizationNameField(el);
}

function isLettersOnlyField(el) {
  if (!el) return false;
  const dataset = el.dataset || {};
  if ((dataset.only || "").toLowerCase() === "letters") return true;
  if (allowsNumericInNameField(el)) return false;
  const name = fieldLabelText(el);
  // Portuguese 'nome' or generic 'name' (pessoas físicas)
  return /\b(nome|name)\b/.test(name);
}

function isDateLikeField(el) {
  if (!el) return false;
  const type = (el.getAttribute("type") || "").toLowerCase();
  if (type === "date") return true;
  const name = (
    el.getAttribute("name") ||
    el.id ||
    el.getAttribute("aria-label") ||
    ""
  ).toLowerCase();
  return /\b(data|date|dt|referencia|referência|inicio|início|fim|periodo|período)\b/.test(
    name
  );
}

function shouldSkipAllGuards(el) {
  return (el?.dataset?.skipGuards || "").toLowerCase() === "true";
}

function sanitizeValueForField(el, value) {
  if (typeof value !== "string") return value;

  let sanitized = removeEmojis(value);

  if (isNumericField(el)) {
    // Keep decimal separators for monetary/percentage inputs
    sanitized = sanitized.replace(/[^0-9,.-]/g, "");
    return sanitized;
  }

  if (isDateLikeField(el)) {
    sanitized = sanitized.replace(ONLY_DATE_CHARS, "");
    return sanitized;
  }

  if (isLettersOnlyField(el)) {
    sanitized = sanitized.replace(ONLY_LETTERS_AND_SPACE, "");
    return sanitized;
  }
  return sanitized;
}

function sanitizeIncomingData(el, data) {
  if (typeof data !== "string") return data;
  return sanitizeValueForField(el, data);
}

// Update the input's value without breaking React's controlled inputs
function setNativeInputValue(el, value) {
  const prototype = Object.getPrototypeOf(el);
  const descriptor = Object.getOwnPropertyDescriptor(prototype, "value");
  if (descriptor && descriptor.set) {
    descriptor.set.call(el, value);
  } else {
    el.value = value;
  }
}

export function attachInputGuards(root = document) {
  if (!root || typeof root.addEventListener !== "function") return () => {};

  const beforeInputHandler = (e) => {
    const target = e.target;
    if (!isTextInput(target) || shouldSkipAllGuards(target)) return;
    // Only process actual text insertions
    if (e.inputType && !e.inputType.startsWith("insert")) return;
    if (typeof e.data !== "string") return; // e.g., deletions
    const sanitized = sanitizeIncomingData(target, e.data);
    if (sanitized !== e.data) {
      // Prevent the invalid insertion and insert sanitized data instead
      e.preventDefault();
      const start = target.selectionStart ?? target.value.length;
      const end = target.selectionEnd ?? target.value.length;
      const next = target.value.slice(0, start) + sanitized + target.value.slice(end);
      setNativeInputValue(target, next);
      const pos = start + sanitized.length;
      try {
        target.setSelectionRange(pos, pos);
      } catch (_) {}
      // Let React know value changed
      target.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  const pasteHandler = (e) => {
    const target = e.target;
    if (!isTextInput(target) || shouldSkipAllGuards(target)) return;
    const clipboard = e.clipboardData?.getData("text");
    if (typeof clipboard !== "string") return;
    const sanitized = sanitizeIncomingData(target, clipboard);
    if (sanitized !== clipboard) {
      e.preventDefault();
      const start = target.selectionStart ?? target.value.length;
      const end = target.selectionEnd ?? target.value.length;
      const next = target.value.slice(0, start) + sanitized + target.value.slice(end);
      setNativeInputValue(target, next);
      const pos = start + sanitized.length;
      try {
        target.setSelectionRange(pos, pos);
      } catch (_) {}
      target.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  const inputHandler = (e) => {
    const target = e.target;
    if (!isTextInput(target) || shouldSkipAllGuards(target)) return;
    const sanitized = sanitizeValueForField(target, String(target.value ?? ""));
    if (sanitized !== target.value) {
      setNativeInputValue(target, sanitized);
      target.dispatchEvent(new Event("input", { bubbles: true }));
    }
  };

  root.addEventListener("beforeinput", beforeInputHandler, true);
  root.addEventListener("paste", pasteHandler, true);
  root.addEventListener("input", inputHandler, true);

  return () => {
    root.removeEventListener("beforeinput", beforeInputHandler, true);
    root.removeEventListener("paste", pasteHandler, true);
    root.removeEventListener("input", inputHandler, true);
  };
}

// Convenience helpers for component-level usage when needed
export const InputGuards = {
  removeEmojis,
  sanitizeValueForField,
};
